import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Users, 
  DollarSign, 
  Target,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Activity,
  PieChart,
  LineChart
} from 'lucide-react';

// Analytics Dashboard Component
export const AnalyticsView = ({ 
  analyticsDashboard = null,
  timeBasedMetrics = {},
  agentPerformance = [],
  conversionFunnel = null,
  revenueAnalytics = null,
  isLoading = false,
  onRefresh = () => {},
  onDateRangeChange = () => {},
  onGenerateReport = () => {},
  onExport = () => {},
  dateRange = { start: '', end: '' },
  selectedPeriod = 'Monthly'
}) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({
    agents: [],
    products: [],
    sources: [],
    carriers: []
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Dashboard Tab Component
  const DashboardTab = () => (
    <div className="space-y-6">
      {/* Date Range and Refresh Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="start-date">Desde:</Label>
            <Input
              id="start-date"
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
              className="w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="end-date">Hasta:</Label>
            <Input
              id="end-date"
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
              className="w-auto"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilterDialog(true)}>
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Key Performance Indicators */}
      {analyticsDashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {Object.entries(analyticsDashboard.stats_cards || {}).map(([key, card]) => (
              <Card key={key}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{card.title}</p>
                      <p className="text-2xl font-bold">
                        {key === 'revenue' ? formatCurrency(card.value) : 
                         key === 'conversion' ? formatPercentage(card.value) :
                         key === 'avg_deal' ? formatCurrency(card.value) :
                         card.value.toLocaleString()}
                      </p>
                      {card.change !== 0 && (
                        <div className={`flex items-center gap-1 text-sm ${getTrendColor(card.trend)}`}>
                          {getTrendIcon(card.trend)}
                          <span>{formatPercentage(Math.abs(card.change))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Alerts and Insights */}
          {(analyticsDashboard.alerts?.length > 0 || analyticsDashboard.insights?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Alerts */}
              {analyticsDashboard.alerts?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                      Alertas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsDashboard.alerts.map((alert, index) => (
                        <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-sm text-red-800">{alert}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insights */}
              {analyticsDashboard.insights?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsDashboard.insights.map((insight, index) => (
                        <div key={index} className="p-3 bg-green-50 border border-green-200 rounded-md">
                          <p className="text-sm text-green-800">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Top Performers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top Agente por Ingresos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsDashboard.top_agent_by_revenue?.name || 'N/A'}</div>
                <p className="text-sm text-gray-600">
                  {formatCurrency(analyticsDashboard.top_agent_by_revenue?.value || 0)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Top Agente por Pólizas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsDashboard.top_agent_by_policies?.name || 'N/A'}</div>
                <p className="text-sm text-gray-600">
                  {analyticsDashboard.top_agent_by_policies?.value || 0} pólizas
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Producto Más Rentable</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsDashboard.top_product_by_revenue || 'N/A'}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Mejor Fuente por Conversión</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsDashboard.top_source_by_conversion || 'N/A'}</div>
              </CardContent>
            </Card>
          </div>

          {/* Trends Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChart className="h-5 w-5" />
                  Tendencia de Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analyticsDashboard.recent_leads_trend?.map((point, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{point.label}</span>
                      <span className="font-medium">{point.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Tendencia de Ingresos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analyticsDashboard.recent_revenue_trend?.map((point, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{point.label}</span>
                      <span className="font-medium">{formatCurrency(point.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Tendencia de Conversión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analyticsDashboard.recent_conversion_trend?.map((point, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{point.label}</span>
                      <span className="font-medium">{formatPercentage(point.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );

  // Agent Performance Tab Component
  const AgentPerformanceTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Performance de Agentes</h3>
        <Button variant="outline" size="sm" onClick={() => onExport('agents')}>
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {agentPerformance.map((agent, index) => (
          <Card key={agent.agent_id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{agent.agent_name}</CardTitle>
                  <CardDescription>
                    Período: {new Date(agent.period_start).toLocaleDateString()} - {new Date(agent.period_end).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Badge variant={agent.rank_by_policies <= 3 ? "default" : "secondary"}>
                    #{agent.rank_by_policies} Pólizas
                  </Badge>
                  <Badge variant={agent.rank_by_revenue <= 3 ? "default" : "secondary"}>
                    #{agent.rank_by_revenue} Ingresos
                  </Badge>
                  <Badge variant={agent.rank_by_conversion <= 3 ? "default" : "secondary"}>
                    #{agent.rank_by_conversion} Conversión
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Leads</p>
                  <p className="text-2xl font-bold">{agent.total_leads}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Conversión</p>
                  <p className="text-2xl font-bold">{formatPercentage(agent.lead_conversion_rate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Cotizaciones</p>
                  <p className="text-2xl font-bold">{agent.total_quotes}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pólizas</p>
                  <p className="text-2xl font-bold">{agent.total_policies}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Ingresos</p>
                  <p className="text-2xl font-bold">{formatCurrency(agent.total_policy_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Comisiones</p>
                  <p className="text-2xl font-bold">{formatCurrency(agent.total_commissions)}</p>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-600">Tiempo Promedio Lead→Cotización</p>
                  <p className="font-medium">{agent.average_lead_to_quote_days.toFixed(1)} días</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tiempo Promedio Cotización→Póliza</p>
                  <p className="font-medium">{agent.average_quote_to_policy_days.toFixed(1)} días</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Ciclo Total Promedio</p>
                  <p className="font-medium">{agent.average_total_cycle_days.toFixed(1)} días</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Conversion Funnel Tab Component
  const ConversionFunnelTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Embudo de Conversión</h3>
        <Button variant="outline" size="sm" onClick={() => onExport('funnel')}>
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {conversionFunnel && (
        <>
          {/* Main Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Embudo Principal</CardTitle>
              <CardDescription>
                Período: {new Date(conversionFunnel.period_start).toLocaleDateString()} - {new Date(conversionFunnel.period_end).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{conversionFunnel.total_leads}</div>
                    <div className="text-sm text-gray-600">Leads Totales</div>
                    <div className="text-xs text-gray-500">100%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{conversionFunnel.contacted_leads}</div>
                    <div className="text-sm text-gray-600">Contactados</div>
                    <div className="text-xs text-gray-500">{formatPercentage(conversionFunnel.contact_rate)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-600">{conversionFunnel.quoted_leads}</div>
                    <div className="text-sm text-gray-600">Cotizados</div>
                    <div className="text-xs text-gray-500">{formatPercentage(conversionFunnel.quote_rate)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">{conversionFunnel.closed_leads}</div>
                    <div className="text-sm text-gray-600">Cerrados</div>
                    <div className="text-xs text-gray-500">{formatPercentage(conversionFunnel.close_rate)}</div>
                  </div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-mg-green">
                    {formatPercentage(conversionFunnel.overall_conversion_rate)}
                  </div>
                  <div className="text-sm text-gray-600">Tasa de Conversión Global</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Funnel by Product */}
          <Card>
            <CardHeader>
              <CardTitle>Conversión por Producto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(conversionFunnel.funnel_by_product || {}).map(([product, data]) => (
                  <div key={product} className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">{product}</h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Leads:</span> {data.total_leads}
                      </div>
                      <div>
                        <span className="text-gray-600">Contactados:</span> {data.contacted_leads} ({formatPercentage(data.contact_rate)})
                      </div>
                      <div>
                        <span className="text-gray-600">Cotizados:</span> {data.quoted_leads} ({formatPercentage(data.quote_rate)})
                      </div>
                      <div>
                        <span className="text-gray-600">Cerrados:</span> {data.closed_leads} ({formatPercentage(data.overall_conversion_rate)})
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Funnel by Source */}
          <Card>
            <CardHeader>
              <CardTitle>Conversión por Fuente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(conversionFunnel.funnel_by_source || {}).map(([source, data]) => (
                  <div key={source} className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2">{source}</h4>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Leads:</span> {data.total_leads}
                      </div>
                      <div>
                        <span className="text-gray-600">Contactados:</span> {data.contacted_leads} ({formatPercentage(data.contact_rate)})
                      </div>
                      <div>
                        <span className="text-gray-600">Cotizados:</span> {data.quoted_leads} ({formatPercentage(data.quote_rate)})
                      </div>
                      <div>
                        <span className="text-gray-600">Cerrados:</span> {data.closed_leads} ({formatPercentage(data.overall_conversion_rate)})
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Time Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Métricas de Tiempo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{conversionFunnel.avg_days_to_contact.toFixed(1)}</div>
                  <div className="text-sm text-gray-600">Días para Contactar</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{conversionFunnel.avg_days_to_quote.toFixed(1)}</div>
                  <div className="text-sm text-gray-600">Días para Cotizar</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{conversionFunnel.avg_days_to_close.toFixed(1)}</div>
                  <div className="text-sm text-gray-600">Días para Cerrar</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{conversionFunnel.avg_total_cycle_days.toFixed(1)}</div>
                  <div className="text-sm text-gray-600">Ciclo Total</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );

  // Revenue Analytics Tab Component
  const RevenueAnalyticsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Análisis de Ingresos</h3>
        <Button variant="outline" size="sm" onClick={() => onExport('revenue')}>
          <Download className="h-4 w-4 mr-2" />
          Exportar
        </Button>
      </div>

      {revenueAnalytics && (
        <>
          {/* Revenue Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Ingresos Totales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(revenueAnalytics.total_revenue)}</div>
                <div className="flex items-center gap-2 mt-2">
                  {getTrendIcon(revenueAnalytics.revenue_growth_rate > 0 ? 'up' : revenueAnalytics.revenue_growth_rate < 0 ? 'down' : 'stable')}
                  <span className={getTrendColor(revenueAnalytics.revenue_growth_rate > 0 ? 'up' : revenueAnalytics.revenue_growth_rate < 0 ? 'down' : 'stable')}>
                    {formatPercentage(Math.abs(revenueAnalytics.revenue_growth_rate))}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Nuevos Negocios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(revenueAnalytics.new_business_revenue)}</div>
                <div className="text-sm text-gray-600 mt-2">
                  {formatPercentage((revenueAnalytics.new_business_revenue / revenueAnalytics.total_revenue) * 100)} del total
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ingresos Recurrentes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(revenueAnalytics.recurring_revenue)}</div>
                <div className="text-sm text-gray-600 mt-2">
                  {formatPercentage((revenueAnalytics.recurring_revenue / revenueAnalytics.total_revenue) * 100)} del total
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Product */}
          <Card>
            <CardHeader>
              <CardTitle>Ingresos por Producto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenueAnalytics.revenue_by_product || {}).map(([product, revenue]) => (
                  <div key={product} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{product}</span>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(revenue)}</div>
                      <div className="text-sm text-gray-600">
                        {formatPercentage((revenue / revenueAnalytics.total_revenue) * 100)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue by Agent */}
          <Card>
            <CardHeader>
              <CardTitle>Ingresos por Agente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenueAnalytics.revenue_by_agent || {}).map(([agent, revenue]) => (
                  <div key={agent} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{agent}</span>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(revenue)}</div>
                      <div className="text-sm text-gray-600">
                        {formatPercentage((revenue / revenueAnalytics.total_revenue) * 100)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue Projections */}
          <Card>
            <CardHeader>
              <CardTitle>Proyecciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-blue-600 font-medium">Próximo Mes</div>
                  <div className="text-2xl font-bold text-blue-800">
                    {formatCurrency(revenueAnalytics.projected_revenue_next_month)}
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-600 font-medium">Próximo Trimestre</div>
                  <div className="text-2xl font-bold text-green-800">
                    {formatCurrency(revenueAnalytics.projected_revenue_next_quarter)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Valor Promedio por Póliza</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(revenueAnalytics.average_policy_value)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ingresos Mensuales Promedio</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(revenueAnalytics.average_monthly_revenue)}</div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );

  // Reports Tab Component
  const ReportsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Reportes Personalizados</h3>
        <Button onClick={() => setShowReportDialog(true)}>
          <BarChart3 className="h-4 w-4 mr-2" />
          Generar Reporte
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Reportes Disponibles</CardTitle>
          <CardDescription>
            Genera reportes personalizados con diferentes métricas y filtros
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte de Ventas</h4>
              <p className="text-sm text-gray-600 mb-3">
                Análisis completo de ventas por período, agente y producto
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('sales')}>
                Generar
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte de Performance</h4>
              <p className="text-sm text-gray-600 mb-3">
                Métricas de rendimiento de agentes y equipos
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('performance')}>
                Generar
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte de Conversión</h4>
              <p className="text-sm text-gray-600 mb-3">
                Análisis detallado del embudo de ventas
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('conversion')}>
                Generar
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte de ROI</h4>
              <p className="text-sm text-gray-600 mb-3">
                Retorno de inversión por fuente y campaña
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('roi')}>
                Generar
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte de Comisiones</h4>
              <p className="text-sm text-gray-600 mb-3">
                Análisis de comisiones pagadas y pendientes
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('commissions')}>
                Generar
              </Button>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-2">Reporte Ejecutivo</h4>
              <p className="text-sm text-gray-600 mb-3">
                Resumen ejecutivo con KPIs principales
              </p>
              <Button size="sm" variant="outline" onClick={() => onGenerateReport('executive')}>
                Generar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-mg-green mb-2">Analytics & Reporting</h1>
        <p className="text-gray-600">
          Análisis comprehensivo y reportes del sistema CRM
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Agentes
          </TabsTrigger>
          <TabsTrigger value="funnel" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Embudo
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Ingresos
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Reportes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab />
        </TabsContent>

        <TabsContent value="agents">
          <AgentPerformanceTab />
        </TabsContent>

        <TabsContent value="funnel">
          <ConversionFunnelTab />
        </TabsContent>

        <TabsContent value="revenue">
          <RevenueAnalyticsTab />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsTab />
        </TabsContent>
      </Tabs>

      {/* Custom Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generar Reporte Personalizado</DialogTitle>
            <DialogDescription>
              Configure los parámetros para su reporte personalizado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="report-name">Nombre del Reporte</Label>
              <Input id="report-name" placeholder="Ej: Análisis de Ventas Q1 2024" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="report-start">Fecha Inicio</Label>
                <Input id="report-start" type="date" />
              </div>
              <div>
                <Label htmlFor="report-end">Fecha Fin</Label>
                <Input id="report-end" type="date" />
              </div>
            </div>
            
            <div>
              <Label>Métricas a Incluir</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {['Leads', 'Quotes', 'Policies', 'Revenue', 'Commissions', 'Conversions'].map(metric => (
                  <div key={metric} className="flex items-center space-x-2">
                    <input type="checkbox" id={`metric-${metric}`} />
                    <Label htmlFor={`metric-${metric}`}>{metric}</Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="export-format">Formato de Exportación</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar formato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={() => setShowReportDialog(false)}>
              Generar Reporte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filtros Avanzados</DialogTitle>
            <DialogDescription>
              Configure filtros para personalizar la vista de analytics
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Agentes</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar agentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los agentes</SelectItem>
                  {agentPerformance.map(agent => (
                    <SelectItem key={agent.agent_id} value={agent.agent_id}>
                      {agent.agent_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Productos</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar productos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los productos</SelectItem>
                  <SelectItem value="Auto">Auto</SelectItem>
                  <SelectItem value="Casa">Casa</SelectItem>
                  <SelectItem value="Comercial">Comercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Fuentes</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar fuentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fuentes</SelectItem>
                  <SelectItem value="Web">Web</SelectItem>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Meta Ads">Meta Ads</SelectItem>
                  <SelectItem value="Google Ads">Google Ads</SelectItem>
                  <SelectItem value="Referencia">Referencia</SelectItem>
                  <SelectItem value="Llamada">Llamada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setShowFilterDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={() => setShowFilterDialog(false)}>
              Aplicar Filtros
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};